import { z } from 'zod';

/*
 * Five-field cron expressions (`minute hour day-of-month month day-of-week`) for schedule
 * triggers (S-07-06). Supports `*`, lists (`1,15`), ranges (`1-5`), steps (`*​/15`, `5/10`,
 * `1-30/5`) and three-letter month / weekday names. No seconds field, no `L` / `W` / `#`, no
 * `@hourly` macros. Pure and dependency-free so it runs in the browser bundle as well as in the
 * timers worker; the next-occurrence arithmetic (time zones) lives in `packages/domain`.
 */

export const CRON_EXPRESSION_MAX = 100;

const MONTH_NAMES = [
  'jan',
  'feb',
  'mar',
  'apr',
  'may',
  'jun',
  'jul',
  'aug',
  'sep',
  'oct',
  'nov',
  'dec',
] as const;
const DAY_NAMES = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'] as const;

interface FieldSpec {
  name: string;
  min: number;
  max: number;
  /** Names accepted instead of numbers; index + `min` is the value. */
  names?: readonly string[];
}

const FIELD_SPECS: readonly FieldSpec[] = [
  { name: 'minute', min: 0, max: 59 },
  { name: 'hour', min: 0, max: 23 },
  { name: 'day-of-month', min: 1, max: 31 },
  { name: 'month', min: 1, max: 12, names: MONTH_NAMES },
  /** 0 and 7 are both Sunday, as in Vixie cron; 7 is folded into 0. */
  { name: 'day-of-week', min: 0, max: 7, names: DAY_NAMES },
];

export interface CronFields {
  minutes: number[];
  hours: number[];
  daysOfMonth: number[];
  months: number[];
  /** 0 = Sunday … 6 = Saturday. */
  daysOfWeek: number[];
  /** The day-of-month field was `*`; with both day fields restricted, cron matches either. */
  anyDayOfMonth: boolean;
  anyDayOfWeek: boolean;
}

export class CronParseError extends Error {
  constructor(
    readonly field: string | null,
    detail: string,
  ) {
    super(field ? `cron ${field}: ${detail}` : `cron: ${detail}`);
    this.name = 'CronParseError';
  }
}

const parseValue = (raw: string, spec: FieldSpec): number => {
  if (/^\d+$/.test(raw)) {
    const n = Number(raw);
    if (n < spec.min || n > spec.max) {
      throw new CronParseError(spec.name, `${raw} is outside ${spec.min}-${spec.max}`);
    }
    return n;
  }
  const idx = spec.names?.indexOf(raw.toLowerCase()) ?? -1;
  if (idx < 0) {
    throw new CronParseError(spec.name, `unexpected value ${raw}`);
  }
  return idx + spec.min;
};

const parseField = (text: string, spec: FieldSpec): number[] => {
  const values = new Set<number>();
  for (const item of text.split(',')) {
    const [rangePart, stepPart, ...rest] = item.split('/');
    if (rangePart === undefined || rangePart === '' || rest.length > 0 || stepPart === '') {
      throw new CronParseError(spec.name, `malformed item ${item}`);
    }
    let step = 1;
    if (stepPart !== undefined) {
      if (!/^\d+$/.test(stepPart) || Number(stepPart) < 1) {
        throw new CronParseError(spec.name, `step must be a positive integer (got ${stepPart})`);
      }
      step = Number(stepPart);
    }
    let lo: number;
    let hi: number;
    if (rangePart === '*') {
      lo = spec.min;
      hi = spec.max;
    } else {
      const [from, to, ...more] = rangePart.split('-');
      if (from === undefined || from === '' || more.length > 0 || to === '') {
        throw new CronParseError(spec.name, `malformed range ${rangePart}`);
      }
      lo = parseValue(from, spec);
      // `5/15` means "from 5 to the end in steps of 15" (Vixie cron).
      hi = to === undefined ? (stepPart === undefined ? lo : spec.max) : parseValue(to, spec);
    }
    if (lo > hi) {
      throw new CronParseError(spec.name, `range ${rangePart} runs backwards`);
    }
    for (let v = lo; v <= hi; v += step) {
      values.add(v);
    }
  }
  return [...values].sort((a, b) => a - b);
};

/** Parses a five-field expression; throws `CronParseError` with the offending field. */
export function parseCron(expression: string): CronFields {
  if (expression.length > CRON_EXPRESSION_MAX) {
    throw new CronParseError(null, `longer than ${CRON_EXPRESSION_MAX} characters`);
  }
  const parts = expression.trim().split(/\s+/);
  if (parts.length !== 5 || parts[0] === '') {
    throw new CronParseError(null, `expected 5 fields, got ${parts[0] === '' ? 0 : parts.length}`);
  }
  const [minute, hour, dayOfMonth, month, dayOfWeek] = parts as [
    string,
    string,
    string,
    string,
    string,
  ];
  const [mSpec, hSpec, domSpec, monSpec, dowSpec] = FIELD_SPECS as [
    FieldSpec,
    FieldSpec,
    FieldSpec,
    FieldSpec,
    FieldSpec,
  ];
  const daysOfWeek = [...new Set(parseField(dayOfWeek, dowSpec).map((d) => (d === 7 ? 0 : d)))];
  daysOfWeek.sort((a, b) => a - b);
  return {
    minutes: parseField(minute, mSpec),
    hours: parseField(hour, hSpec),
    daysOfMonth: parseField(dayOfMonth, domSpec),
    months: parseField(month, monSpec),
    daysOfWeek,
    anyDayOfMonth: dayOfMonth === '*',
    anyDayOfWeek: dayOfWeek === '*',
  };
}

export const isValidCron = (expression: string): boolean => {
  try {
    parseCron(expression);
    return true;
  } catch {
    return false;
  }
};

export interface CronInstant {
  minute: number;
  hour: number;
  dayOfMonth: number;
  /** 1-12. */
  month: number;
  /** 0 = Sunday. */
  dayOfWeek: number;
}

/** True when the wall-clock instant (already in the schedule's time zone) matches the fields. */
export function cronMatches(fields: CronFields, at: CronInstant): boolean {
  if (!fields.minutes.includes(at.minute) || !fields.hours.includes(at.hour)) {
    return false;
  }
  if (!fields.months.includes(at.month)) {
    return false;
  }
  const domHit = fields.daysOfMonth.includes(at.dayOfMonth);
  const dowHit = fields.daysOfWeek.includes(at.dayOfWeek);
  if (fields.anyDayOfMonth && fields.anyDayOfWeek) {
    return true;
  }
  if (fields.anyDayOfMonth) {
    return dowHit;
  }
  if (fields.anyDayOfWeek) {
    return domHit;
  }
  return domHit || dowHit;
}

/** Zod form used by the `schedule` trigger. Issues carry the offending field in the message. */
export const CronExpressionSchema = z
  .string()
  .trim()
  .min(9)
  .max(CRON_EXPRESSION_MAX)
  .superRefine((value, ctx) => {
    try {
      parseCron(value);
    } catch (error) {
      ctx.addIssue({
        code: 'custom',
        message: error instanceof Error ? error.message : 'invalid cron expression',
      });
    }
  });
